import React from 'react'
import Select from 'react-select'
import { useHistory } from 'react-router-dom'
import { useGetAllBusinesses, useGetBusinessIDIfExists } from '@marketing-milk/frontend'

interface Props {
  expanded: boolean
  /** Collapsed, the control is an icon; clicking it opens the rail to reveal the real one. */
  onRequestExpand: () => void
}

type Option = { value: number; label: string }

// react-select paints itself white by default, which reads as a hole punched in
// the rail. These override only what the dark ground needs.
const darkSelectStyles = {
  // The switcher sits above the section list, so anything it contributes to
  // the block's height shunts every link below it the moment a peek opens.
  // The rail fixes that height; these make the control fill it exactly rather
  // than assert a second one that has to be kept in agreement.
  container: (base: any) => ({ ...base, width: '100%' }),
  control: (base: any) => ({
    ...base,
    minHeight: 0,
    height: '100%',
    backgroundColor: '#1E1B4B',
    borderColor: '#332E81',
    boxShadow: 'none',
    ':hover': { borderColor: '#4138CA' },
  }),
  singleValue: (base: any) => ({ ...base, color: '#F3F4F6', fontSize: 13 }),
  placeholder: (base: any) => ({ ...base, color: '#9CA3AF', fontSize: 13 }),
  input: (base: any) => ({ ...base, color: '#F3F4F6' }),
  menu: (base: any) => ({ ...base, backgroundColor: '#1E1B4B', zIndex: 60 }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: 13,
    backgroundColor: state.isFocused ? '#332E81' : 'transparent',
    color: '#F3F4F6',
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base: any) => ({ ...base, padding: 4, color: '#9CA3AF' }),
}

// Collapsed, the rail has no room for the business name, and a generic building
// glyph answers "there is a business" rather than "which one".
function initials(name: string): string {
  return name
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join('')
}

export const RailClientSelector = ({ expanded, onRequestExpand }: Props) => {
  const history = useHistory()
  const businessID = useGetBusinessIDIfExists()
  const businesses = useGetAllBusinesses()

  const options: Option[] = businesses.map(b => ({ value: b.id, label: b.businessName }))
  const value = businessID ? options.find(o => o.value === Number(businessID)) ?? null : null

  function onSelect(option: Option) {
    // Swapping business keeps the user on the same kind of page rather than
    // dumping them back at a list.
    const newURL = history.location.pathname.replace(`/${businessID}/`, `/${option.value}/`)
    history.push(newURL)
  }

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={onRequestExpand}
        title={value ? value.label : 'Pick a business'}
        aria-label={value ? value.label : 'Pick a business'}
        className="flex h-10 w-10 items-center justify-center rounded-lg border border-mm-secondary-800 bg-mm-secondary-900 transition-colors hover:border-mm-secondary-700 hover:bg-mm-secondary-800"
      >
        {value ? (
          <span className="text-xs font-semibold tracking-wide text-mm-neutral-1-50">
            {initials(value.label)}
          </span>
        ) : (
          <i className="fas fa-building text-lg text-mm-secondary-200" aria-hidden />
        )}
      </button>
    )
  }

  return (
    <Select
      options={options}
      value={value}
      onChange={onSelect as any}
      styles={darkSelectStyles}
      placeholder="Pick a business"
      isDisabled={!businessID}
      menuPlacement="auto"
      aria-label="Business"
    />
  )
}
